import { useContext, useEffect, useRef, useState } from "react";
import { Box, Icon, IconButton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import DriftingOrbs from "./DriftingOrbs.js";
import { getProductPage } from "src/components/product/productContent.js";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import { railArrowSx, scrollPageCount, scrollPageIndex } from "src/utils/emblaUtils.js";

/** How long each page of cards holds before auto-advancing. */
const PRODUCT_ADVANCE_MS = 8000;

/** Joins a rail item to its registry page for card copy. Null if the slug is unknown. */
const cardData = (item) => {
  const page = getProductPage(item.slug);
  if (!page?.hero?.title) return null;
  return {
    slug: item.slug,
    eyebrow: page.eyebrow,
    title: page.hero.title,
    blurb: item.blurb ?? page.blurb,
  };
};

/**
 * Big-type poster card for a product page, over a drifting-orb background.
 * Adjacent cards alternate cool/warm palettes.
 *
 * @param {{ data: object, index: number, surface: string, user?: object, compact?: boolean }} props
 */
const ProductPosterCard = ({ data, index, surface, user, compact = false }) => {
  const onClick = () =>
    customEvent({
      name: "product_card_click",
      category: "Product",
      label: `${data.slug} (${surface})`,
      data: {
        ...(user ? { user_id: user._id, user_email: user.email } : {}),
      },
    });

  return (
    <Box
      component={RouterLink}
      to={`/product/${data.slug}`}
      onClick={onClick}
      data-testid={`product-card-${data.slug}`}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "22px",
        backgroundColor: "#fff",
        minHeight: { xs: compact ? 140 : 200, md: 172 },
        p: { xs: compact ? 2 : 2.75, md: 3 },
        display: "flex",
        flexDirection: "column",
        textDecoration: "none",
        color: "text.primary",
        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
        transition: "transform 0.3s cubic-bezier(0.2,0.7,0.2,1), box-shadow 0.3s ease",
        "&:hover": { transform: "translateY(-4px)", boxShadow: "0 10px 30px rgba(0,0,0,0.12)" },
        "@media (prefers-reduced-motion: reduce)": { "&:hover": { transform: "none" } },
      }}
    >
      <DriftingOrbs palette={index % 2 === 0 ? "cool" : "warm"} />
      <Box sx={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", flex: 1 }}>
        <Typography sx={{ fontSize: "0.66rem", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "text.secondary" }}>
          {data.eyebrow}
        </Typography>
        <Typography component="h3" sx={{ fontSize: { xs: compact ? "1.35rem" : "1.85rem", md: "2rem" }, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.02, mt: 1 }}>
          {data.title}
        </Typography>
        <Typography sx={{ fontSize: "0.88rem", color: "text.secondary", lineHeight: 1.45, mt: 1, maxWidth: "34ch" }}>
          {data.blurb}
        </Typography>
        <Box sx={{ mt: "auto", pt: 1.5, display: "inline-flex", alignItems: "center", gap: 1.1, fontSize: "0.82rem", fontWeight: 800 }}>
          Learn more
          <Box sx={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: "text.primary", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon baseClassName="material-symbols-outlined" sx={{ fontSize: "1.15rem" }}>arrow_forward</Icon>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * A rail of product poster cards: a paged scroll-snap carousel with dots —
 * two cards per page on desktop, one per swipe on mobile. Sits on the page
 * background (no band).
 *
 * @param {{ heading: string, items: Array<{ slug: string, blurb: string }>, surface: string,
 *   compact?: boolean, headingSx?: object }} props
 */
const ProductPosterRail = ({ heading, items, surface, compact = false, headingSx }) => {
  const { user } = useContext(AuthContext);
  const scrollerRef = useRef(null);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const pausedRef = useRef(false);

  const cards = items.map(cardData).filter(Boolean);
  const cardCount = cards.length;

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const measure = () => setPageCount(scrollPageCount(el));
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [cardCount]);

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (reducedMotion || pageCount < 2) return undefined;
    const timer = setInterval(() => {
      const el = scrollerRef.current;
      if (!el || pausedRef.current) return;
      const next = scrollPageIndex(el) + 1;
      el.scrollTo({ left: next >= pageCount ? 0 : next * el.clientWidth, behavior: "smooth" });
    }, PRODUCT_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [reducedMotion, pageCount]);

  if (!cards.length) return null;

  return (
    <Box
      sx={{ mb: { xs: 5, md: 7 } }}
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onTouchStart={() => { pausedRef.current = true; }}
    >
      {heading && (
        <Typography variant="sectionTitle" component="h2" sx={{ mb: 2, ...headingSx }}>
          {heading}
        </Typography>
      )}
      <Box sx={{ position: "relative" }}>
      <Box
        ref={scrollerRef}
        onScroll={(event) => {
          const el = event.currentTarget;
          setPage(scrollPageIndex(el));
        }}
        sx={{
          display: "flex",
          gap: { xs: 2, md: 3 },
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          overscrollBehaviorX: "none",
          // mobile: bleeds to the screen edges
          mx: { xs: -2, md: 0 },
          px: { xs: 2, md: 0 },
          // snap positions must track the bleed padding so the first card rests aligned with the heading
          scrollPadding: { xs: "0 16px", md: "0" },
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "& > *": {
            flex: {
              xs: compact ? "0 0 74%" : "0 0 86%",
              md: "0 0 calc((100% - 24px) / 2)",
            },
            minWidth: 0,
            boxSizing: "border-box",
            scrollSnapAlign: "start",
          },
        }}
      >
        {cards.map((data, index) => (
          <ProductPosterCard key={data.slug} data={data} index={index} surface={surface} user={user} compact={compact} />
        ))}
      </Box>
      {/* Desktop page arrows, matching the homepage rail chrome. */}
      {pageCount > 1 && page > 0 && (
        <IconButton
          onClick={() => {
            const el = scrollerRef.current;
            el?.scrollTo({ left: (page - 1) * el.clientWidth, behavior: "smooth" });
          }}
          aria-label={`Scroll ${heading || "products"} left`}
          sx={posterArrowSx("left")}
        >
          <ChevronLeft />
        </IconButton>
      )}
      {pageCount > 1 && page < pageCount - 1 && (
        <IconButton
          onClick={() => {
            const el = scrollerRef.current;
            el?.scrollTo({ left: (page + 1) * el.clientWidth, behavior: "smooth" });
          }}
          aria-label={`Scroll ${heading || "products"} right`}
          sx={posterArrowSx("right")}
        >
          <ChevronRight />
        </IconButton>
      )}
      </Box>
      {pageCount > 1 && (
        <Box sx={{ display: "flex", gap: 0.75, justifyContent: "center", mt: 1.5 }}>
          {Array.from({ length: pageCount }, (_, index) => (
            <Box
              key={index}
              onClick={() => {
                const el = scrollerRef.current;
                el?.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
              }}
              sx={{
                height: 6,
                width: index === page ? 17 : 6,
                borderRadius: 3,
                cursor: "pointer",
                backgroundColor: index === page ? "text.primary" : "overlay.black15",
                transition: "all 0.2s ease",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * Floating page arrow: the shared rail-arrow chrome, repositioned for this
 * non-bleeding card carousel (both sides hang in the gutter, centered, desktop only).
 *
 * @param {"left"|"right"} side
 * @returns {object} An MUI `sx` object.
 */
const posterArrowSx = (side) => ({
  ...railArrowSx(side),
  display: { xs: "none", md: "inline-flex" },
  [side]: "-16px",
  top: "50%",
});

export default ProductPosterRail;
